import { FC, useEffect, useRef, useState } from "react";
import { BaselineDialogProps, FormType } from "../../common";
import { DialogContainer } from "../../CommonDataParts/DialogContainer2";
import { useIntl } from "../../../../../../_metronic/i18n/customUseIntl";
import { processAndStageStylesV2 } from "../../../../../../_metronic/layout";
import { CSSClassesList } from "../../../../helpers/helperTypes";
import { DairyIntervention } from "../../../../models/Intervention";
import { useFormContext } from "react-hook-form";
import { DsmGrid } from "@dsm-dcs/design-system-react";
import {
  DairyV2Baseline,
  DairyV2ContinuousGrowingStageData,
  DairyV2OutputContinuousGrowing
} from "../../../../models/Baseline/DairyV2Baseline";
import DairyV2OutputContinuousGrowingForm from "./DairyV2OutputContinuousGrowingForm";
import DsmButtonControlGroup from "../../../helpers/DsmButtonControlGroup";
import { cloneDeep } from "lodash";
import EditModeButtons from "../../../helpers/EditModeButtons";
import { Spacer } from "../../../helpers/Spacer";

interface DairyV2OutputContinuousGrowingDialogProps extends BaselineDialogProps {
  formType: FormType;
  stageIndex: number;
  baseline: DairyV2Baseline;
  handleCancel: (param?: string | null) => void;
}

const DairyV2OutputContinuousGrowingDialog: FC<DairyV2OutputContinuousGrowingDialogProps> = ({
  formType = FormType.Add,
  stageIndex = 0,
  baseline,
  handleCancel,
}) => {
  const intl = useIntl();
  const classes: CSSClassesList = processAndStageStylesV2();
  const formTitle = intl.formatMessage({ id: 'SUSTELL.PROCESS.DIALOG.STAGE.OUTPUT.TITLE', });

  const fieldItemPrefix = `stages.${stageIndex}.stageData.output`;

  const currResetValue = useRef<DairyV2OutputContinuousGrowing>();
  const fc = useFormContext<DairyIntervention>();

  const currentStageData = baseline.stages[stageIndex].stageData;
  const dairyV2ContinuousGrowingOutputData = (currentStageData as DairyV2ContinuousGrowingStageData).output;

  const [isOutputEdit, setisOutputEdit] = useState<boolean>(false);

  useEffect(() => {
    if (isOutputEdit && currResetValue.current === undefined)
      currResetValue.current = cloneDeep(fc.getValues(fieldItemPrefix));
  }, [isOutputEdit]);

  const resetEdit = () => {
    if (currResetValue.current !== undefined) {
      for (let [propertyName] of Object.entries(dairyV2ContinuousGrowingOutputData)) {
        if (currResetValue.current![propertyName as keyof DairyV2OutputContinuousGrowing] === undefined)
          fc.setValue(`${fieldItemPrefix}.${propertyName}`, "");
      }
    }

    setisOutputEdit(false);
  };

  const handleDialogCancelClick = () => {
    resetEdit();

    fc.setValue(`${fieldItemPrefix}`, currResetValue.current);

    if (handleCancel) {
      handleCancel();
    }
  };

  return (
    <DialogContainer
      formVisible={true}
      handleClose={handleDialogCancelClick}
      iconCode="arrows/arrow-circle-broken-right"
      formTitle={formTitle}
      datasetType="intervention"
      width={isOutputEdit ? "592px" : "460px"}
    >
      <DsmGrid className={classes.dsmGridOneColumnWithBorder}>
        {dairyV2ContinuousGrowingOutputData && (
          <>
            <Spacer />
            <DairyV2OutputContinuousGrowingForm
              editEnabled={isOutputEdit}
              stageIndex={stageIndex}
              baseline={baseline}
            />
          </>
        )}
        {formType !== FormType.View &&
          <EditModeButtons
            isEditing={isOutputEdit}
            onEdit={() => setisOutputEdit(true)}
            onCancel={resetEdit}
          />}
      </DsmGrid>
      <DsmButtonControlGroup
        cancelHandler={handleDialogCancelClick}
        saveHandler={handleCancel}
        saveLabel={intl.formatMessage({ id: 'GENERAL.CONFIRM' })}
      />
    </DialogContainer>
  )
};

export default DairyV2OutputContinuousGrowingDialog;
